import { easternIsoDay } from '../../data/taskRules';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useAppUser } from '../../auth/AppUserContext';
import { MONTH_NAMES } from '../../audit/budgets';
import { getStoreBudgets, saveBudgets, type StoreBudgetRow } from '../../data/storeBudgets';
import type { TemplateRow } from '../../data/templateAdmin';
import type { Store } from '../../data/types';
import { ActionButton } from '../ActionButton';
import { budgetTemplatesFor, canEditBudgets, type BudgetTemplate } from './budgetAccess';
import { QUARTER_NAMES, monthsIn, monthsOf, parseBudgetText, withMonths, type MonthView } from './budgetImport';
import { MonthViewSwitch, useMonthView } from './MonthViewSwitch';

const textOf = (v: number | null) => (v === null ? '' : String(v));

/**
 * The store page's "Budgets and Goals" (B8, owner rulings 2026-10-25: budgets
 * belong on each store's page too): this store's monthly KPI budgets for a
 * year, per template that applies to it. Everyone who sees the store reads
 * them; Admins and the store's DM can edit here (bulk edit + CSV live in
 * the Budgets and Goals page).
 */
export function StoreBudgetsSection({ store, templateRows }: { store: Store; templateRows: readonly TemplateRow[] }) {
  const { user } = useAppUser();
  const thisYear = Number(easternIsoDay(new Date()).slice(0, 4));
  const [year, setYear] = useState(thisYear);
  const [rows, setRows] = useState<StoreBudgetRow[] | null>(null);
  const [failed, setFailed] = useState(false);
  const storeId = Number(store.id);
  // Templates with unsaved edits: changing the year or leaving asks first (QA 2026-10-25).
  const [dirty, setDirty] = useState<ReadonlySet<string>>(new Set());
  const markDirty = useCallback((key: string, on: boolean) => {
    setDirty((prev) => {
      if (prev.has(key) === on) return prev;
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);
  useEffect(() => {
    if (dirty.size === 0) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const load = async () => setRows(await getStoreBudgets(storeId));
  useEffect(() => {
    let live = true;
    setRows(null);
    setFailed(false);
    getStoreBudgets(storeId)
      .then((r) => live && setRows(r))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [storeId]);

  // UI review 2 (V1/V2/V7): a quarter at a time where twelve months don't fit (phones: three labelled
  // months per KPI), "All 12" on wide screens — read-only and Edit alike.
  const [view, setView] = useMonthView(year, true);
  const templates = useMemo(() => budgetTemplatesFor(templateRows, store), [templateRows, store]);
  if (templates.length === 0) return null;
  const row = rows?.find((r) => r.year === year) ?? null;
  const editable = canEditBudgets(user, store);
  const hasAny = templates.some((t) => t.columns.some((c) => monthsOf(row?.budgets, t.key, c.id).some((v) => v !== null)));

  return (
    <section className="dash-section store-budgets">
      <div className="page-head">
        <h2 style={{ margin: 0 }}>Budgets and Goals</h2>
        <span className="page-head-tools">
          <select
            value={year}
            onChange={(e) => {
              if (dirty.size > 0 && !window.confirm('Discard your unsaved changes?')) return;
              setDirty(new Set());
              setYear(Number(e.target.value));
            }}
            aria-label="Year"
          >
            {[thisYear - 1, thisYear, thisYear + 1].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          {user?.role !== 'StoreTeam' && <Link to="/budgets">All stores →</Link>}
        </span>
      </div>
      {/* The month switch only when there's something to show or edit. */}
      {!failed && rows !== null && (editable || hasAny) && <MonthViewSwitch view={view} onChange={setView} />}
      {failed ? (
        <p className="muted">Budgets and goals couldn’t be loaded — refresh to try again.</p>
      ) : rows === null ? (
        <p>Loading…</p>
      ) : (
        templates.map((t) => (
          <TemplateBudgets
            key={`${t.key}:${year}`}
            template={t}
            store={store}
            year={year}
            view={view}
            row={row}
            editable={editable}
            onSaved={load}
            onDirty={markDirty}
          />
        ))
      )}
    </section>
  );
}

function TemplateBudgets({
  template,
  store,
  year,
  view,
  row,
  editable,
  onSaved,
  onDirty,
}: {
  template: BudgetTemplate;
  store: Store;
  year: number;
  view: MonthView;
  row: StoreBudgetRow | null;
  editable: boolean;
  onSaved: () => Promise<void>;
  onDirty: (key: string, dirty: boolean) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Map<string, string[]>>(new Map());
  useEffect(() => onDirty(template.key, draft.size > 0), [draft, onDirty, template.key]);
  useEffect(() => () => onDirty(template.key, false), [onDirty, template.key]);
  const [error, setError] = useState<string | null>(null);
  const textAt = (kpiId: string, m: number) => draft.get(kpiId)?.[m] ?? textOf(monthsOf(row?.budgets, template.key, kpiId)[m]!);
  const empty = template.columns.every((c) => monthsOf(row?.budgets, template.key, c.id).every((v) => v === null));

  const save = async () => {
    setError(null);
    const bad = [...draft.values()].some((r) => r.some((t) => parseBudgetText(t) === 'bad'));
    if (bad) {
      // A bad cell may sit in a quarter that isn't shown — say where (as the Budgets page does).
      const where = [...new Set([...draft.values()].flatMap((r) => r.flatMap((t, m) => (parseBudgetText(t) === 'bad' ? [QUARTER_NAMES[Math.floor(m / 3)]!] : []))))].sort();
      setError(`Fix the cells that aren’t numbers first (${where.join(', ')}).`);
      return;
    }
    let budgets = row?.budgets;
    for (const [kpiId, texts] of draft) {
      budgets = withMonths(budgets, template.key, kpiId, texts.map((t) => parseBudgetText(t) as number | null));
    }
    try {
      const { failed } = await saveBudgets([{ storeId: Number(store.id), storeNumber: store.storeNumber, year, id: row?.id ?? null, ...(row?.eTag ? { eTag: row.eTag } : {}), budgets: budgets ?? {} }]);
      if (failed.length > 0) {
        // Show what's saved now (someone else's change, or the row a race created); the edits stay.
        await onSaved().catch(() => undefined);
        setError(`Not saved: ${failed[0]!.reason}.`);
        return;
      }
      await onSaved();
      setDraft(new Map());
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="store-budget-template">
      <div className="page-head">
        <h3 style={{ margin: 0 }}>{template.name}</h3>
        {editable && !editing && (
          <button type="button" className="btn btn-small" onClick={() => setEditing(true)}>
            Edit
          </button>
        )}
      </div>
      {empty && !editing ? (
        <p className="muted">No budgets or goals set yet for {year}.</p>
      ) : (
        <div className="budget-grid-wrap">
          <table className={view === 'all' ? 'budget-grid budget-12' : 'budget-grid'}>
            <thead>
              <tr>
                <th className="budget-store">KPI</th>
                {monthsIn(view).map((m) => (
                  <th key={m}>{MONTH_NAMES[m]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {template.columns.map((c) => (
                <tr key={c.id} className={draft.has(c.id) ? 'budget-dirty' : undefined}>
                  <td className="budget-store">
                    {c.label}
                    {c.type === 'percent' && !c.label.includes('%') && <span className="muted"> (%)</span>}
                  </td>
                  {monthsIn(view).map((m) => {
                    const name = MONTH_NAMES[m]!;
                    const text = textAt(c.id, m);
                    return (
                      // Phones: each KPI is a card with its months labelled (UI review 2, V1/V2).
                      <td key={name} data-label={name}>
                        {editing ? (
                          <input
                            className={parseBudgetText(text) === 'bad' ? 'budget-input input-required' : 'budget-input'}
                            inputMode="decimal"
                            aria-label={`${c.label} ${name}`}
                            value={text}
                            onChange={(e) =>
                              setDraft((prev) => {
                                const next = new Map(prev);
                                const r = [...(prev.get(c.id) ?? monthsOf(row?.budgets, template.key, c.id).map(textOf))];
                                r[m] = e.target.value;
                                next.set(c.id, r);
                                return next;
                              })
                            }
                          />
                        ) : (
                          <span className={text ? undefined : 'muted'}>{text || '—'}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {error && <p className="error">{error}</p>}
      {editing && (
        <div className="modal-actions">
          <button
            type="button"
            className="btn"
            onClick={() => {
              setDraft(new Map());
              setEditing(false);
              setError(null);
            }}
          >
            Cancel
          </button>
          <ActionButton className="btn btn-primary" disabled={draft.size === 0} busyLabel="Saving…" onAction={save}>
            Save
          </ActionButton>
        </div>
      )}
    </div>
  );
}
